/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Real-time Coherence Monitor Component
 *
 * Displays live Φ (phi) coherence metrics with visual indicators
 * Can be embedded in any page for monitoring system health
 */

import React, { useState, useEffect, useRef } from 'react'
import { Activity, Brain, Zap, Shield, AlertTriangle, Check, TrendingUp, TrendingDown } from 'lucide-react'

interface CoherenceData {
  phi: number
  phiEma: number
  components: {
    messageFlow: number
    taskCompletion: number
    errorRate: number
    memoryUtilization: number
  }
  trend: 'up' | 'down' | 'stable'
  status: 'OPTIMAL' | 'GOOD' | 'DEGRADED' | 'CRITICAL'
  timestamp: number
}

interface CoherenceMonitorProps {
  updateInterval?: number // milliseconds
  compact?: boolean
  showHistory?: boolean
  showComponents?: boolean
  onStatusChange?: (status: string) => void
  websocketUrl?: string
}

export const CoherenceMonitor: React.FC<CoherenceMonitorProps> = ({
  updateInterval = 1000,
  compact = false,
  showHistory = true,
  showComponents = true,
  onStatusChange,
  websocketUrl = 'ws://localhost:8090/ws/coherence'
}) => {
  const [coherenceData, setCoherenceData] = useState<CoherenceData>({
    phi: 0.92,
    phiEma: 0.89,
    components: {
      messageFlow: 0.95,
      taskCompletion: 0.88,
      errorRate: 0.93,
      memoryUtilization: 0.91
    },
    trend: 'stable',
    status: 'OPTIMAL',
    timestamp: Date.now()
  })

  const [history, setHistory] = useState<number[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // WebSocket connection management
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        wsRef.current = new WebSocket(websocketUrl)

        wsRef.current.onopen = () => {
          console.log('Coherence monitor connected')
          setIsConnected(true)
          setReconnectAttempts(0)
        }

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            updateCoherence(data)
          } catch (error) {
            console.error('Failed to parse coherence data:', error)
          }
        }

        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error)
        }

        wsRef.current.onclose = () => {
          setIsConnected(false)
          wsRef.current = null

          // Exponential backoff reconnect
          if (reconnectAttempts < 5) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000)
            reconnectTimeoutRef.current = setTimeout(() => {
              setReconnectAttempts(prev => prev + 1)
              connectWebSocket()
            }, delay)
          }
        }
      } catch (error) {
        console.error('Failed to connect WebSocket:', error)
        // Fallback to simulated data
        simulateData()
      }
    }

    connectWebSocket()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [websocketUrl, reconnectAttempts])

  // Fallback simulation when WebSocket unavailable
  const simulateData = () => {
    const interval = setInterval(() => {
      const baseValue = 0.85 + Math.random() * 0.1
      const noise = (Math.random() - 0.5) * 0.05

      setCoherenceData(prev => {
        const newPhi = Math.max(0, Math.min(1, baseValue + noise))
        const newEma = prev.phiEma * 0.9 + newPhi * 0.1

        // Determine status based on EMA
        let status: CoherenceData['status'] = 'OPTIMAL'
        if (newEma < 0.5) status = 'CRITICAL'
        else if (newEma < 0.7) status = 'DEGRADED'
        else if (newEma < 0.85) status = 'GOOD'

        // Determine trend
        const trend = newPhi > prev.phi ? 'up' : newPhi < prev.phi ? 'down' : 'stable'

        return {
          phi: newPhi,
          phiEma: newEma,
          components: {
            messageFlow: Math.max(0, Math.min(1, 0.9 + (Math.random() - 0.5) * 0.2)),
            taskCompletion: Math.max(0, Math.min(1, 0.85 + (Math.random() - 0.5) * 0.2)),
            errorRate: Math.max(0, Math.min(1, 0.95 - Math.random() * 0.1)),
            memoryUtilization: Math.max(0, Math.min(1, 0.7 + Math.random() * 0.2))
          },
          trend,
          status,
          timestamp: Date.now()
        }
      })
    }, updateInterval)

    return () => clearInterval(interval)
  }

  // Update coherence data and history
  const updateCoherence = (data: Partial<CoherenceData>) => {
    setCoherenceData(prev => {
      const updated = { ...prev, ...data, timestamp: Date.now() }

      // Notify parent of status changes
      if (onStatusChange && updated.status !== prev.status) {
        onStatusChange(updated.status)
      }

      return updated
    })

    // Update history for sparkline
    if (showHistory) {
      setHistory(prev => {
        const newHistory = [...prev, data.phi || coherenceData.phi]
        return newHistory.slice(-50) // Keep last 50 points
      })
    }
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPTIMAL': return 'text-green-400 bg-green-500/20'
      case 'GOOD': return 'text-blue-400 bg-blue-500/20'
      case 'DEGRADED': return 'text-yellow-400 bg-yellow-500/20'
      case 'CRITICAL': return 'text-red-400 bg-red-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  // Get component health color
  const getComponentColor = (value: number) => {
    if (value >= 0.9) return 'text-green-400'
    if (value >= 0.7) return 'text-blue-400'
    if (value >= 0.5) return 'text-yellow-400'
    return 'text-red-400'
  }

  // Render sparkline
  const renderSparkline = () => {
    if (!showHistory || history.length < 2) return null

    const width = compact ? 100 : 200
    const height = compact ? 30 : 50
    const points = history.map((value, index) => {
      const x = (index / (history.length - 1)) * width
      const y = height - (value * height)
      return `${x},${y}`
    }).join(' ')

    return (
      <svg width={width} height={height} className="opacity-75">
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={coherenceData.trend === 'up' ? 'text-green-400' : coherenceData.trend === 'down' ? 'text-red-400' : 'text-blue-400'}
        />
      </svg>
    )
  }

  // Compact view for embedding in headers/sidebars
  if (compact) {
    return (
      <div className="flex items-center space-x-3 p-3 bg-gray-900/50 rounded-lg border border-gray-800">
        <div className="relative">
          <Brain className="w-5 h-5 text-gray-400" />
          {isConnected && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-300">Φ</span>
            <span className={`text-lg font-bold ${getComponentColor(coherenceData.phi)}`}>
              {coherenceData.phi.toFixed(3)}
            </span>
            {coherenceData.trend === 'up' && <TrendingUp className="w-3 h-3 text-green-400" />}
            {coherenceData.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-400" />}
          </div>
        </div>

        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(coherenceData.status)}`}>
          {coherenceData.status}
        </span>
      </div>
    )
  }

  // Full view
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Brain className="w-8 h-8 text-blue-400" />
            {isConnected && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">System Coherence</h3>
            <p className="text-xs text-gray-400">
              {isConnected ? 'Live monitoring' : `Simulated (reconnecting... ${reconnectAttempts}/5)`}
            </p>
          </div>
        </div>

        <div className={`px-3 py-1.5 rounded-lg font-medium ${getStatusColor(coherenceData.status)}`}>
          {coherenceData.status}
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Φ Now</span>
            {coherenceData.trend === 'up' && <TrendingUp className="w-4 h-4 text-green-400" />}
            {coherenceData.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" />}
            {coherenceData.trend === 'stable' && <Activity className="w-4 h-4 text-blue-400" />}
          </div>
          <div className="text-2xl font-bold text-white">
            {coherenceData.phi.toFixed(4)}
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Φ EMA</span>
            <Zap className="w-4 h-4 text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {coherenceData.phiEma.toFixed(4)}
          </div>
        </div>
      </div>

      {/* Sparkline History */}
      {showHistory && history.length > 0 && (
        <div className="mb-6 p-4 bg-gray-800/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">50-point history</span>
            <span className="text-xs text-gray-500">
              Min: {Math.min(...history).toFixed(3)} / Max: {Math.max(...history).toFixed(3)}
            </span>
          </div>
          {renderSparkline()}
        </div>
      )}

      {/* Component Breakdown */}
      {showComponents && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-400">Components</h4>

          <div className="space-y-2">
            {Object.entries(coherenceData.components).map(([key, value]) => {
              const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
              const percentage = (value * 100).toFixed(1)

              return (
                <div key={key} className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400">{label}</span>
                      <span className={`text-xs font-medium ${getComponentColor(value)}`}>
                        {percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-500 ${
                          value >= 0.9 ? 'bg-green-500' :
                          value >= 0.7 ? 'bg-blue-500' :
                          value >= 0.5 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    {value >= 0.9 ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : value >= 0.5 ? (
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    ) : (
                      <Shield className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="mt-4 pt-4 border-t border-gray-800">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">
            Last update: {new Date(coherenceData.timestamp).toLocaleTimeString()}
          </span>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-gray-400">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CoherenceMonitor